import { forwardRef, useMemo } from 'react';
import { TextField as MUITextField, TextFieldProps } from '@mui/material';
import { EventType } from '@/lib/types';
import {
  compactFilledBorderedInputFixedBoxSx,
  compactFilledFloatingLabelSx,
  compactFilledInputValueSx,
  defaultFilledFloatingLabelSx,
  defaultFilledInputValueSx,
} from '@/lib/compactFilledFieldStyles';
import { Skeleton } from '../Skeleton';
import debounce from 'lodash/debounce';

const DEBOUNCE_DELAY = 500;

type TrackingEvent = {
  name: string;
  properties: Record<string, unknown>;
};

type DeprecatedTextFieldPropName =
  | 'InputProps'
  | 'inputProps'
  | 'InputLabelProps'
  | 'FormHelperTextProps';

export type CustomTextFieldProps = Omit<
  TextFieldProps,
  DeprecatedTextFieldPropName
> & {
  loading?: boolean;
  trackChange?: (event: TrackingEvent) => void;
  trackFocus?: (event: TrackingEvent) => void;
  trackEventName?: string;
  eventType?: EventType;
  InputProps?: any;
  inputProps?: any;
  InputLabelProps?: any;
  FormHelperTextProps?: any;
};

export const TextField = forwardRef<HTMLDivElement, CustomTextFieldProps>(
  function TextField(
    {
      loading = false,
      trackChange,
      trackFocus,
      trackEventName,
      eventType = EventType.INPUT_CHANGE,
      InputProps: legacyInputProps,
      inputProps: legacyHtmlInput,
      InputLabelProps: legacyInputLabelProps,
      FormHelperTextProps: legacyFormHelperTextProps,
      slotProps,
      ...props
    },
    ref,
  ) {
    // Debounce function to prevent excessive logging
    const debouncedTrackChange = useMemo(() => {
      return debounce((value: string) => {
        if (trackChange && trackEventName) {
          trackChange({
            name: trackEventName,
            properties: {
              eventType,
              fieldName: trackEventName,
              fieldValue: value,
            },
          });
        }
      }, DEBOUNCE_DELAY);
    }, [trackChange, trackEventName, eventType]);

    if (loading) {
      return <Skeleton variant="text" />;
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value ?? '';

      // Use debounced function for tracking
      debouncedTrackChange(value);

      props.onChange?.(event);
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      if (trackFocus && trackEventName) {
        trackFocus({
          name: trackEventName,
          properties: {
            eventType: EventType.INPUT_FOCUS,
            fieldName: trackEventName,
          },
        });
      }
      props.onFocus?.(event);
    };

    // Accept both the legacy InputProps/inputProps/InputLabelProps and the
    // modern slotProps API, folding the legacy props into slotProps so
    // callers can use either while we migrate off the deprecated props.
    const baseSlotProps: Record<string, any> = {
      ...(slotProps as Record<string, any> | undefined),
    };
    const inputSlot: Record<string, any> = {
      ...legacyInputProps,
      ...(baseSlotProps.input as Record<string, any> | undefined),
    };
    const htmlInputSlot: Record<string, any> = {
      ...legacyHtmlInput,
      ...(baseSlotProps.htmlInput as Record<string, any> | undefined),
    };
    const inputLabelSlot: Record<string, any> = {
      ...legacyInputLabelProps,
      ...(baseSlotProps.inputLabel as Record<string, any> | undefined),
    };
    const formHelperTextSlot: Record<string, any> = {
      ...legacyFormHelperTextProps,
      ...(baseSlotProps.formHelperText as Record<string, any> | undefined),
    };

    if (props.variant === 'filled') {
      const isSmall = props.size === 'small';
      const filledLabelSx = isSmall
        ? compactFilledFloatingLabelSx
        : defaultFilledFloatingLabelSx;
      const filledInputTypographySx = isSmall
        ? compactFilledInputValueSx
        : defaultFilledInputValueSx;

      return (
        <MUITextField
          {...props}
          ref={ref}
          margin={isSmall ? 'none' : props.margin}
          required={props.required}
          error={props.disabled ? false : props.error}
          onChange={handleChange}
          onFocus={handleFocus}
          slotProps={{
            ...baseSlotProps,
            inputLabel: {
              shrink: true,
              ...inputLabelSlot,
              sx: {
                ...filledLabelSx,
                ...inputLabelSlot.sx,
              },
            },
            input: {
              disableUnderline: true,
              ...inputSlot,
              sx: {
                ...filledInputTypographySx,
                // The fixed-height box is for single-line inputs only; applying it
                // to a multiline field collapses the textarea to one row.
                ...(isSmall && !props.multiline
                  ? compactFilledBorderedInputFixedBoxSx
                  : {}),
                backgroundColor: 'background.paper',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                ':focus-within': {
                  borderColor: 'primary.main',
                },
                ':has(input[aria-invalid="true"])': {
                  borderColor: 'error.main',
                },
                '&.MuiFilledInput-root': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  fontStyle: 'italic',
                  '& input': {
                    bgcolor: 'transparent',
                    WebkitTextFillColor: 'currentColor',
                  },
                },
                ...inputSlot.sx,
              },
            },
            htmlInput: htmlInputSlot,
            formHelperText: formHelperTextSlot,
          }}
          sx={{
            overflow: 'hidden',
            borderRadius: '8px',
            ...props.sx,
          }}
        />
      );
    }

    return (
      <MUITextField
        {...props}
        ref={ref}
        onChange={handleChange}
        onFocus={handleFocus}
        slotProps={{
          ...baseSlotProps,
          ...(legacyInputProps ? { input: inputSlot } : {}),
          ...(legacyHtmlInput ? { htmlInput: htmlInputSlot } : {}),
          ...(legacyInputLabelProps ? { inputLabel: inputLabelSlot } : {}),
          ...(legacyFormHelperTextProps
            ? { formHelperText: formHelperTextSlot }
            : {}),
        }}
      />
    );
  },
);
